import { Role } from "@models/role.model";
import { Permission } from "@models/permission.model";
import { Permissions } from "@models/permissions.enum";
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  Output,
  EventEmitter,
} from "@angular/core";
import { RoleService } from "../shared/role.service";
import { ToastrService } from "ngx-toastr";
import { RoleStateService } from "../shared/role-state.service";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";

@Component({
  selector: "app-editrole",
  templateUrl: "./editrole.component.html",
  styleUrls: ["./editrole.component.css"],
})
export class EditroleComponent implements OnInit, OnChanges {
  @Input() selectedRole: Role;
  @Output() roleUpdated = new EventEmitter<any>();
  @Output() roleDeleted = new EventEmitter<string>();

  permissions: Array<Permission>;
  groupedPermissions: { [key: string]: Permission[] } = {};
  permissionGroups: string[] = [];
  selectedCategories: string[] = [];

  // Hierarchical permission configuration
  // Maps parent permissions to their children that should auto-grant the parent
  private permissionHierarchy: { [parent: string]: string[] } = {
    // Clients Read - granted by any of these child read permissions
    [Permissions.ClientsRead]: [
      Permissions.ClientsProfileRead,
      Permissions.ClientsComplianceRead,
      Permissions.ClientsNotesRead,
      Permissions.ClientsBoardsRead,
      Permissions.ClientsBillToRead,
      Permissions.ClientsBillToProfileRead,
      Permissions.ClientsBillToContactsRead,
      Permissions.ClientsBillToBillingRead,
      Permissions.ClientsChange,
      Permissions.ClientsProfileChange,
      Permissions.ClientsComplianceChange,
      Permissions.ClientsNotesChange,
      Permissions.ClientsBoardsChange,
      Permissions.ClientsBillToChange,
      Permissions.ClientsBillToProfileChange,
      Permissions.ClientsBillToContactsChange,
      Permissions.ClientsBillToBillingChange,
    ],
    [Permissions.ClientsChange]: [
      Permissions.ClientsProfileChange,
      Permissions.ClientsComplianceChange,
      Permissions.ClientsNotesChange,
      Permissions.ClientsBoardsChange,
      Permissions.ClientsBillToChange,
      Permissions.ClientsBillToProfileChange,
      Permissions.ClientsBillToContactsChange,
      Permissions.ClientsBillToBillingChange,
    ],
    // Drivers Read - granted by any of these child read permissions
    [Permissions.DriversRead]: [
      Permissions.DriversProfileRead,
      Permissions.DriversComplianceRead,
      Permissions.DriversAssignmentsRead,
      Permissions.DriversNotesRead,
      Permissions.DriversDocumentsRead,
      Permissions.DriversSettlementsRead,
      Permissions.DriversTeamsRead,
      Permissions.DriversScheduleRead,
      Permissions.DriversChange,
      Permissions.DriversProfileChange,
      Permissions.DriversComplianceChange,
      Permissions.DriversAssignmentsChange,
      Permissions.DriversNotesChange,
      Permissions.DriversDocumentsChange,
      Permissions.DriversSettlementsChange,
      Permissions.DriversTeamsChange,
      Permissions.DriversScheduleChange,
    ],
    [Permissions.DriversChange]: [
      Permissions.DriversProfileChange,
      Permissions.DriversComplianceChange,
      Permissions.DriversAssignmentsChange,
      Permissions.DriversNotesChange,
      Permissions.DriversDocumentsChange,
      Permissions.DriversSettlementsChange,
      Permissions.DriversTeamsChange,
      Permissions.DriversScheduleChange,
    ],
  };

  // Basic client permissions that should always be visible
  private basicClientPermissions = [Permissions.ClientsRead, Permissions.ClientsChange];

  // Basic driver permissions that should always be visible
  private basicDriverPermissions = [Permissions.DriversRead, Permissions.DriversChange];

  constructor(
    private _roleService: RoleService,
    private toastr: ToastrService,
    private roleStateService: RoleStateService,
    private featureFlagsService: FeatureFlagsService
  ) {}

  ngOnInit() {
    this._roleService.getAllRoleRules().subscribe((permissions: Array<Permission>) => {
      this.permissions = permissions;
      this.groupPermissions();
    });
  }

  groupPermissions() {
    const groups: { [key: string]: Permission[] } = {};
    const clientsFeatureEnabled = this.featureFlagsService.isEnabled("clients");
    const driversFeatureEnabled = this.featureFlagsService.isEnabled("drivers");

    this.permissions.forEach((permission) => {
      const name = permission.name;
      let group = "Other";

      if (name.startsWith("Clients")) {
        // Filter out advanced client permissions if feature flag is disabled
        if (!clientsFeatureEnabled) {
          const isBasicPermission = this.basicClientPermissions.some(
            (basicPerm) => basicPerm === name
          );
          if (!isBasicPermission) {
            return; // Skip this permission
          }
        }
        group = "Clients";
      } else if (name.startsWith("Lessor")) group = "Lessor";
      else if (
        name.startsWith("Products") ||
        name.startsWith("Client Products") ||
        name.startsWith("Equipment Codes")
      )
        group = "Products & Equipment";
      else if (name.startsWith("Charge Types")) group = "Charge Types";
      else if (name.startsWith("Rates")) group = "Rates";
      else if (name.startsWith("PeopleNet")) group = "PeopleNet";
      else if (name.startsWith("Accessorials") || name.startsWith("Locations"))
        group = "Accessorials & Locations";
      else if (name.startsWith("T-Chek")) group = "T-Chek";
      else if (name.startsWith("Dashboard")) group = "Dashboard";
      else if (
        name.startsWith("Billing") ||
        name.startsWith("Cancelled Trips") ||
        name.startsWith("Ready For Bill") ||
        name.startsWith("Billed") ||
        name.startsWith("Cancelled Read")
      )
        group = "Billing";
      else if (name.startsWith("Drivers")) {
        // Filter out advanced driver permissions if feature flag is disabled
        if (!driversFeatureEnabled) {
          const isBasicPermission = this.basicDriverPermissions.some(
            (basicPerm) => basicPerm === name
          );
          if (!isBasicPermission) {
            return; // Skip this permission
          }
        }
        group = "Drivers";
      } else if (name.startsWith("Tractors")) group = "Tractors";
      else if (name.startsWith("Trailers")) group = "Trailers";
      else if (
        name.startsWith("Fuel Exports") ||
        name.startsWith("Invoice Exports") ||
        name.startsWith("Batch Exports") ||
        name.startsWith("Various Reports")
      )
        group = "Reports";
      else if (name.startsWith("Settlements") || name.startsWith("Batch"))
        group = "Settlements & Batch";
      else if (name.startsWith("PC Miler")) group = "PC Miler";
      else if (
        name.startsWith("Users") ||
        name.startsWith("Roles") ||
        name.startsWith("Views") ||
        name.startsWith("Config")
      )
        group = "System Administration";
      else if (name.startsWith("Trip") || name.startsWith("Other Trip")) group = "Trips";
      else if (name === "Admin") group = "Administration";
      else if (name.startsWith("Boards")) group = "Boards";
      else if (name.startsWith("Dispatch")) group = "Dispatch";
      else if (name.startsWith("Customer Exports")) group = "Customer Exports";

      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(permission);
    });

    // Sort groups alphabetically
    this.permissionGroups = Object.keys(groups).sort();
    this.groupedPermissions = groups;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedRole && !changes.selectedRole.firstChange) {
      // Initialize the permissions to the newly selected role
      const rolePermissions = changes.selectedRole.currentValue.Permissions;
      this.selectedCategories = rolePermissions.map((perm) => perm.name);
      // Ensure parent permissions are included for any existing child permissions
      this.ensureParentPermissions();
    }
  }

  /**
   * Called when a permission checkbox changes.
   * Ensures that parent permissions are automatically selected when a child is selected.
   */
  onPermissionChange(): void {
    this.ensureParentPermissions();
  }

  /**
   * Adds parent permissions when any of their child permissions are selected.
   * Uses the permissionHierarchy configuration to determine relationships.
   */
  private ensureParentPermissions(): void {
    for (const [parent, children] of Object.entries(this.permissionHierarchy)) {
      const hasChild = this.selectedCategories.some((perm) => children.includes(perm));
      if (hasChild && !this.selectedCategories.includes(parent)) {
        this.selectedCategories.push(parent);
      }
    }
  }

  updateRole() {
    const roleName = this.selectedRole.rolName;
    const permissions = this.selectedCategories;
    const rolePermissions = { roleName, permissions };
    this._roleService.updateRoleRules(rolePermissions).subscribe(
      (result) => {
        this.toastr.success("Role updated", "Success!");
        this.roleUpdated.emit(result);
        this.roleStateService.refreshRoles().subscribe();
        this.close();
      },
      (error) => {
        this.toastr.warning("error", "Alert!", {
          closeButton: true,
          enableHtml: true,
        });
        console.dir(error);
      }
    );
  }

  deleteRole() {
    if (!confirm(`Are you sure you want to delete the role "${this.selectedRole.rolName}"?`)) {
      return;
    }

    const roleId = this.selectedRole.id?.toString();
    this._roleService.delete(roleId).subscribe(
      (result) => {
        this.toastr.success("Role deleted", "Success!");
        this.roleDeleted.emit(roleId);
        this.close();
      },
      (error) => {
        console.dir(error);
        this.toastr.error(error || "Error deleting role");
      }
    );
  }

  close() {
    const closeModal = document.getElementsByClassName("p-dialog-header-close")[0] as HTMLElement;
    closeModal.click();
  }
}
